import React from 'react';
import RouteLoading from 'Components/RouteLoading';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import GlobalPollerManager from 'Components/GlobalPollerManager/GlobalPollerManager';
import BatchPollingManager from 'Components/Wls/BatchScreening/BatchPollingManager';
import Routes from './routes/Routes';
import { useAppStore } from './store';
import { setAxiosBaseUrl } from './services/apiService';
import { isScreeningAxiosInitialized, setAxiosScreeningUrl } from './services/screeningApiService';

/**
 * React Query
 * - staleTime: Prevent refetching data in the background.
 * - cacheTime: Garbage collected get determined by cacheTime, default is 5 minutes.
 * - refetchOnWindowFocus: If set to true, the query will refetch on window focus if the data is stale.
 *
 * @see https://tanstack.com/query/v4/docs/reference/QueryClient
 * @note If the query exists and the data is not invalidated or older than the given staleTime,
 * then the data from the cache will be returned. Otherwise it will try to fetch the latest data.
 */

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 2,
            cacheTime: 1000 * 60 * 5,
            refetchOnWindowFocus: true,
            retry: 0
        }
    }
});

const Devtools = React.lazy(() =>
    import('@tanstack/react-query-devtools').then((mod) => ({ default: mod.ReactQueryDevtools }))
);

export default function App() {
    const appConfig = useAppStore.use.appConfig?.();
    const authConfig = useAppStore.use.authConfig?.();
    const userDetails = useAppStore.use.userDetails?.();
    const fetchConfig = useAppStore.use.fetchConfig?.();
    const authService = useAppStore.use.authService?.();
    const isAuthenticated = useAppStore.use.isAuthenticated?.();
    const fetchUser = useAppStore.use.fetchUser?.();
    const fetchAccessToken = useAppStore.use.fetchAccessToken?.();
    const [isScreeningAxiosReady, setIsScreeningAxiosReady] = React.useState(false);

    React.useEffect(() => {
        const script = document.createElement('script');

        script.src = appConfig?.dynaTraceUrl;
        script.type = 'text/javascript';
        script.crossOrigin = 'anonymous';

        if (appConfig?.dynaTraceUrl) {
            document.head.appendChild(script);
        }
    }, [appConfig?.dynaTraceUrl]);

    React.useEffect(() => {
        setAxiosBaseUrl(appConfig?.baseApiURL);

        const screeningApiURL = appConfig?.screeningApiURL;
        const customerId = userDetails?.customerId;

        if (!screeningApiURL || !customerId) {
            setIsScreeningAxiosReady(false);
            return;
        }

        const screeningUrlWithCustomerId = `${screeningApiURL}-${encodeURIComponent(customerId)}`;

        setAxiosScreeningUrl(screeningUrlWithCustomerId);
        setIsScreeningAxiosReady(isScreeningAxiosInitialized());
    }, [appConfig?.baseApiURL, appConfig?.screeningApiURL, userDetails?.customerId]);

    React.useEffect(() => {
        let unsubscribe = false;

        const setConfig = async () => {
            if (!unsubscribe) {
                await fetchConfig();
            }
        };
        setConfig();

        return () => {
            unsubscribe = true;
        };
    }, [fetchConfig]);

    React.useEffect(() => {
        let unsubscribe = false;

        const userAuthentication = async () => {
            if (!unsubscribe && appConfig) {
                await fetchUser();
            }
        };
        userAuthentication();

        return () => {
            unsubscribe = true;
        };
    }, [fetchUser, appConfig]);

    React.useEffect(() => {
        let unsubscribe = false;
        if (!unsubscribe && authService) {
            fetchAccessToken();
        }

        return () => {
            unsubscribe = true;
        };
    }, [authService, fetchAccessToken]);

    if (!appConfig || !authConfig || !authService || !isAuthenticated) {
        return <RouteLoading />;
    }

    return (
        <QueryClientProvider client={queryClient}>
            <GlobalPollerManager />
            {isScreeningAxiosReady && <BatchPollingManager />}
            <Routes authConfig={authConfig} />
            {process.env.NODE_ENV === 'development' && (
                <React.Suspense fallback={null}>
                    <Devtools position='bottom-right' initialIsOpen={false} />
                </React.Suspense>
            )}
        </QueryClientProvider>
    );
}
